import React, {useEffect, useState} from "react";
import {Button, Col, message, Row, Space, Spin, Statistic, Typography, Tooltip, Select, Card} from "antd";
import {ClockCircleOutlined, ReloadOutlined} from "@ant-design/icons";
import {
    Chart,
    LineElement,
    PointElement,
    LinearScale,
    Title as ChartTitle,
    CategoryScale,
    BarElement,
    Legend,
    TimeSeriesScale,
    ChartData,
    Point,
} from 'chart.js';
import dayjs, {Dayjs} from "dayjs";
import {blue, red, green, yellow, geekblue, purple, magenta, grey, gold, orange} from '@ant-design/colors';
import {Line} from "react-chartjs-2";
import type {ChartOptions} from "chart.js/dist/types";


import {AnalyticsService} from "../../../../services";
import {weekendDatesHighlighter} from "utils/graphweekends";
import {
    DATE_RANGE_FILTER_SELECTOR_OPTIONS,
    DateRangeFilterSelectorOption,
    DateRangeFilterSelectorOptionsLabel,
} from "../../../../helpers/dates-helper";
import DateRangeSelector from "../../../../components/DateRangeSelector";


Chart.register(LineElement, BarElement, PointElement, LinearScale, CategoryScale, ChartTitle, TimeSeriesScale, Legend);


type AppActiveUsersMetricProps = any


interface MetricType {
    operatingSystem: string
    date: string
    activeUsers: number
    adoptionRate?: number // only for second chart
}

const {Title} = Typography
const colorShade = 3 // colors shade
const allColors = [
    blue[colorShade],
    red[colorShade],
    green[colorShade],
    yellow[colorShade],
    geekblue[colorShade],
    purple[colorShade],
    magenta[colorShade],
    grey[colorShade],
    gold[colorShade],
    orange[colorShade]
]; // antd colors pallet

type UsagePeriod = "daily" | "weekly" | "monthly"

// GA emits the rolling metrics per-day, so all three keep a daily x-axis
const ACTIVE_USERS_METRIC: Record<UsagePeriod, string> = {
    daily: "activeUsers",
    weekly: "active7DayUsers",
    monthly: "active28DayUsers",
}

const USAGE_PERIOD_LABEL: Record<UsagePeriod, string> = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
}


const AppActiveUsersMetric: React.FC<AppActiveUsersMetricProps> = () => {
    // todo: refactor
    const [data, setData] = useState<MetricType[]>()
    const [loading, setLoading] = useState<boolean>(false)

    // Used to switch between absolute values and percentage
    const [showPercentage, setShowPercentage] = useState(true);

    // Used to switch between daily, weekly (trailing 7-day) and monthly (trailing 28-day) active users
    const [usagePeriod, setUsagePeriod] = useState<UsagePeriod>("daily");

    // Selected date range (preset or custom). Defaults to "Last 30 Days".
    const [selectedDateRange, setSelectedDateRange] = useState<DateRangeFilterSelectorOption>(
        DATE_RANGE_FILTER_SELECTOR_OPTIONS[1]
    );
    const dateRange = selectedDateRange.value;


    // useEffect that listens for changes in date range and usage period
    useEffect(() => {
        fetchData()
    }, [selectedDateRange, usagePeriod]); // Dependency array


    const fetchData = () => {
        setLoading(true)
        // Weekly and monthly active users use GA's trailing 7-day / 28-day rolling metrics
        const metric = ACTIVE_USERS_METRIC[usagePeriod]
        const request = {
            dimensions: ["operatingSystem", "date"],
            metrics: [metric],
            start_date: dateRange?.[0]?.format("YYYY-MM-DD"),
            end_date: dateRange?.[1]?.format("YYYY-MM-DD"),
        }
        AnalyticsService.getGoogleAnalytics<MetricType>(request)
            .then(res => {
                // Normalize the metric field so downstream code always reads `activeUsers`
                const normalized: MetricType[] = res.data.map((item: any) => ({
                    ...item,
                    activeUsers: Number(item[metric] ?? item.activeUsers ?? 0),
                }))
                setData(normalized)
            })
            .catch(err => {
                console.error("Error fetching data, message: ", err)
                message.error("Error fetching data")
            })
            .finally(() => {
                setLoading(false)
            })

    }

    const formatDate = (dd: string) => {
        return dayjs(dd, "YYYYMMDD").format("YYYY-MM-DD")
    }


    const handleDateRangeChange = (label: string, dates: [Dayjs | null, Dayjs | null]) => {
        setSelectedDateRange({label: label as DateRangeFilterSelectorOptionsLabel, value: dates});
    };


    // ------ Users By Operating System over time ------


    const calculateTotalUsersPerDay = (data: MetricType[]): { [k: string]: number } => {
        const totalsPerDay: { [k: string]: number } = {};
        data.forEach((item: MetricType) => {
            totalsPerDay[formatDate(item.date)] = (totalsPerDay[formatDate(item.date)] || 0) + Number(item.activeUsers);
        });

        return totalsPerDay;
    };


    const filterData = (rawData: MetricType[]): MetricType[] => {
        const totalUsersPerDay = calculateTotalUsersPerDay(rawData);
        // filter last 1 day
        // get top 10 based on operating system 
        const endDate = dateRange?.[1]?.add(-1, 'd').format("YYYY-MM-DD") || ''
        const topN: string[] = rawData.filter(item => formatDate(item.date).localeCompare(endDate) >= 0)
            .sort((a: MetricType, b: MetricType) => {
                return Number(b.activeUsers) - Number(a.activeUsers)
            })
            .slice(0, 10)
            .map(item => item.operatingSystem)
        const filteredData = rawData.filter(item => topN.includes(item.operatingSystem)).sort((a: MetricType, b: MetricType) => {
                return Number(a.date) - Number(b.date)
            }
        )

        filteredData.forEach(item => {
            item.adoptionRate = Number(item.activeUsers) / totalUsersPerDay[formatDate(item.date)];

        });
        return filteredData

    }

    const usersByOperatingSystemsOverTimeData = data ? filterData(data) : []
    let colorIndex = 0;
    const uniqueDates = Array.from(new Set(usersByOperatingSystemsOverTimeData?.map(d => formatDate(d.date))));

    // Group Data by App Version
    interface GroupedDataType {
        [key: string]: {
            date: string,
            metric: number | undefined
        }[]
    }

    const groupedData = usersByOperatingSystemsOverTimeData.reduce((acc: GroupedDataType, curr) => {

        const operatingSystem = curr.operatingSystem;
        if (!acc[operatingSystem]) {
            acc[operatingSystem] = [];
        }
        acc[operatingSystem].push({
            date: curr.date,
            metric: showPercentage ? curr.adoptionRate : curr.activeUsers
        });
        return acc;
    }, {});

    // Step 2: Create Chart Datasets
    const datasets = Object.keys(groupedData).map(operatingSystem => {
        return {
            label: operatingSystem,
            data: groupedData[operatingSystem].map(d => ({x: formatDate(d.date), y: d.metric})),
            fill: false,
            borderColor: allColors[colorIndex++ % allColors.length],
            backgroundColor: '#fff',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 5,
        };
    });


    const lastDayValues: { [key: string]: number } = {};
    usersByOperatingSystemsOverTimeData.forEach(metric => {
        if (formatDate(metric.date) === uniqueDates[uniqueDates.length - 1]) {
            lastDayValues[metric.operatingSystem] = metric.activeUsers;
        }
    });


    const chartData: ChartData<"line", (number | Point | null)[], string> = {
        labels: uniqueDates || [],
        datasets: datasets
    };

    const chartOptions: ChartOptions<any> = {
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'day',
                    tooltipFormat: 'dd MMM yyyy',
                    displayFormats: {
                        day: 'dd MMM'
                    }
                },
                grid: {
                    display: false
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value: any) {
                        return showPercentage ? `${value * 100}%` : value;
                    }
                }
            }
        },
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true, // Use the point style instead of the default box
                    pointStyle: 'circle', // Set the point style to circle
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += showPercentage ? `${100 * context.parsed.y.toFixed(2)}%` : context.parsed.y;
                        }
                        return label;
                    }
                }
            }
        },
        tension: 0.4,
    };

    // ------ End Users By Operating System over time ------

    // ------ All active users ------
    const sumOfActiveUsers: { [k: string]: number } = {};
    usersByOperatingSystemsOverTimeData.forEach(metric => {
        const date = formatDate(metric.date);
        if (!sumOfActiveUsers[date]) {
            sumOfActiveUsers[date] = 0;
        }
        sumOfActiveUsers[date] += Number(metric.activeUsers);
    });

    chartData.datasets.push({
        label: 'All active users',
        data: uniqueDates.map((date: string) => showPercentage ? 1 : sumOfActiveUsers[date]),
        fill: false,
        borderColor: allColors[colorIndex++ % allColors.length],
        backgroundColor: '#fff',
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
    });
    // ------ End All active users ------

    // ------ Averages over the selected period ------
    const numDays = uniqueDates.length || 1;

    // Total active users per operating system across the whole period
    const totalsPerOs: { [os: string]: number } = {};
    usersByOperatingSystemsOverTimeData.forEach(metric => {
        totalsPerOs[metric.operatingSystem] = (totalsPerOs[metric.operatingSystem] || 0) + Number(metric.activeUsers);
    });

    // Average per day for a given OS name (case-insensitive match on the GA dimension)
    const averageForOs = (osName: string): number => {
        const total = Object.keys(totalsPerOs)
            .filter(os => os.toLowerCase() === osName.toLowerCase())
            .reduce((sum, os) => sum + totalsPerOs[os], 0);
        return Math.round(total / numDays);
    };

    const avgAndroid = averageForOs("Android");
    const avgIos = averageForOs("iOS");
    const avgAll = Math.round(
        Object.values(sumOfActiveUsers).reduce((sum, v) => sum + v, 0) / numDays
    );

    const periodLabel = USAGE_PERIOD_LABEL[usagePeriod];
    const averagesTitle = `Avg ${periodLabel} Active Users`;
    // ------ End Averages ------


    return (
        <div>


            <Card title="App Active users"
                  loading={loading}
                  extra={
                      <>
                          <Tooltip title="Updated Now">
                              <ClockCircleOutlined style={{marginRight: 10}}/>
                          </Tooltip>

                          <Select
                              defaultValue={showPercentage ? "Rate" : "Count"}
                              style={{width: 100, marginRight: 8}}
                              onChange={(value) => setShowPercentage(value === "Rate")}
                          >
                              <Select.Option value="Count">Count</Select.Option>
                              <Select.Option value="Rate">Rate %</Select.Option>
                          </Select>

                          <Select
                              value={usagePeriod}
                              style={{width: 100, marginRight: 8}}
                              onChange={(value) => setUsagePeriod(value)}
                          >
                              <Select.Option value="daily">Daily</Select.Option>
                              <Select.Option value="weekly">Weekly</Select.Option>
                              <Select.Option value="monthly">Monthly</Select.Option>
                          </Select>

                          <DateRangeSelector
                              defaultDateRange={selectedDateRange}
                              onDateRangeChange={handleDateRangeChange}
                          />


                          <Button
                              size="middle"
                              loading={loading}
                              onClick={fetchData}
                              icon={<ReloadOutlined/>}
                              style={{marginRight: 8}}
                          />

                      </>
                  }>

                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={8}>
                                <Statistic title={`${averagesTitle} — Android (${selectedDateRange.label})`}
                                           value={avgAndroid}/>
                            </Col>
                            <Col span={8}>
                                <Statistic title={`${averagesTitle} — iOS (${selectedDateRange.label})`}
                                           value={avgIos}/>
                            </Col>
                            <Col span={8}>
                                <Statistic title={`${averagesTitle} — All Users (${selectedDateRange.label})`}
                                           value={avgAll}/>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Space direction="horizontal">
                                    <Title level={5}>Active Users By Operating System Over Time</Title>
                                </Space>
                                <Spin spinning={loading}>
                                    <div className="w-100 d-bloc" style={{minHeight: 400}}>
                                        {
                                            loading ?
                                                <p>Loading...</p> :
                                                data && <Line data={chartData} height={150} options={chartOptions}
                                                              plugins={[weekendDatesHighlighter]}/>
                                        }
                                    </div>
                                </Spin>
                            </Col>

                        </Row>
                    </Col>
                </Row>
            </Card>


        </div>
    )

}

export default AppActiveUsersMetric  